import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  Alert,
  Button,
  Card,
  Input,
  PageHeading,
  Stack,
} from "~/components";
import { useCreateWorkflow, makeModuleNode } from "~/lib/workflows";

export const Route = createFileRoute("/_app/workflows/new")({
  component: NewWorkflowPage,
});

function NewWorkflowPage() {
  const navigate = useNavigate();
  const createMutation = useCreateWorkflow();
  const [name, setName] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    createMutation.mutate(
      { name: trimmed, definition: makeModuleNode(trimmed) },
      {
        onSuccess: (workflow) => {
          void navigate({
            to: "/workflows/$id/edit",
            params: { id: workflow.id },
          });
        },
      },
    );
  };

  return (
    <Stack gap="lg">
      <PageHeading
        title="New workflow"
        subtitle="Create a blank workflow. Phases and tasks are added in the editor after creation."
        actions={
          <Link to="/workflows">
            <Button type="button" variant="secondary">
              Back to workflows
            </Button>
          </Link>
        }
      />
      <Card>
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Stack gap="xs">
              <label htmlFor="workflow-name">
                <strong>Workflow name</strong>
              </label>
              <Input
                id="workflow-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., PI Case Lifecycle"
                required
                autoFocus
              />
            </Stack>

            <small>
              You'll add phases and tasks after creating the workflow.
            </small>

            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={createMutation.isPending || name.trim().length === 0}
              >
                {createMutation.isPending ? "Creating…" : "Create workflow"}
              </Button>
            </div>

            {createMutation.isError && (
              <Alert variant="danger" icon={<AlertTriangle size={18} />}>
                Failed to create workflow. {createMutation.error.message}
              </Alert>
            )}
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
